import { Box, Chip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { ReportInstance } from '../../../backendsdk';
import { TrackedDataGrid as StyledDataGrid } from '../../../components/TrackedComponents';
import useIsMobile from '../../../hooks/isMobile';
import useProfile from '../../../hooks/profile';
import { getDateTimeColumnType, localeByCountryMap } from '../../../utils/DataGridDateTime';
import palette from '../../ColorPalette';
import { gridLocalization } from '../OTA/MuiDeviceTable';
import { REPORT_TYPE_TO_URL_PARAM } from './ReportComponent';
import { DEVICE_FILTER_OPTIONS, deviceFilter, getExtraFilters } from './ReportDetails';
import { ASSIGNEE_OPTIONS, REVERSED_SEVERITY_FILTER_VALUES } from './ReportOptions';

interface PastReportsProps {
    reportInstances: ReportInstance[];
    geofences: Record<string, string>;
    isLoading: boolean;
    onRowClick: CallableFunction;
}

const PastReports: React.FC<PastReportsProps> = (props: PastReportsProps) => {
    const { i18n, t } = useTranslation();
    const { profile } = useProfile();
    const isMobile = useIsMobile();
    const history = useHistory();
    const locale = localeByCountryMap[profile.locale];

    const getGroupedFilters = (tKey: string, key: string, values: Array<string>) => {
        const formattedKey = t(`content.reports.form.${tKey}.${key.toLocaleLowerCase()}`, key);
        if (values.length === 0) {
            return { key: formattedKey };
        } else if (values.length > 1) {
            return { key: `${values.length} ${t(`content.reports.form.${tKey}.${key.toLocaleLowerCase()}_plural`)}` };
        } else {
            return {
                key: formattedKey,
                value: t(`content.reports.form.filter.${values[0].toLocaleLowerCase()}`, values[0]),
            };
        }
    };

    const getUngroupedFilters = (tKey: string, key: string, values: Array<string> | string) => {
        let formattedValues;
        if (key != DEVICE_FILTER_OPTIONS.ALL) {
            if (key.toLocaleLowerCase() === 'assignee' && values.includes(ASSIGNEE_OPTIONS.CUSTOMER)) {
                formattedValues = [profile.customer.name];
            } else if (key.toLocaleLowerCase() === 'severity' && typeof values === 'string') {
                if (values in REVERSED_SEVERITY_FILTER_VALUES) {
                    const valueName =
                        REVERSED_SEVERITY_FILTER_VALUES[values as keyof typeof REVERSED_SEVERITY_FILTER_VALUES];
                    formattedValues = [t(`content.reports.form.filter.${valueName.toLocaleLowerCase()}`)];
                } else {
                    formattedValues = [values];
                }
            } else if (key.toLocaleLowerCase() === 'geofence' && values.length > 0) {
                formattedValues = (values as string[]).map((s: string) => props.geofences[s]);
            } else {
                formattedValues = (values as string[]).map((s: string) =>
                    t(`content.reports.form.filter.${s.toLocaleLowerCase()}`, s),
                );
            }
        }
        return {
            key: t(`content.reports.form.${tKey}.${key.toLocaleLowerCase()}`, key),
            values: formattedValues,
        };
    };

    const formatScope = (scope: string) => {
        return t(`content.reports.form.scope.${scope.toLocaleLowerCase()}`);
    };

    const formatFilters = (tKey: string, key: string, values: Array<string> | string, groupValues = false) => {
        if (groupValues) {
            const res = getGroupedFilters(tKey, key, values as string[]);
            return res.value ? `${res.key}: ${res.value}` : res.key;
        }
        const res = getUngroupedFilters(tKey, key, values);
        return res.values ? `${res.key}: ${res.values.join(', ')}` : res.key;
    };

    const renderFilters = (tKey: string, key: string, values: Array<string> | string, groupValues = false) => {
        if (groupValues) {
            const res = getGroupedFilters(tKey, key, values as string[]);
            return (
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{res.key}</span>
                    {res.value && <span>: {res.value}</span>}
                </div>
            );
        }
        const res = getUngroupedFilters(tKey, key, values);
        return (
            <Box key={key} sx={{ display: 'flex' }}>
                <span style={{ fontWeight: 'bold', paddingTop: '4px', whiteSpace: 'nowrap' }}>{res.key}</span>
                {res.values && (
                    <>
                        <span style={{ paddingTop: '4px', marginRight: '4px' }}>:</span>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                            {res.values.map((value) => {
                                return (
                                    <Chip
                                        size="small"
                                        label={<Typography fontSize={12}>{value}</Typography>}
                                        key={value}
                                        sx={{ mr: 0.5, my: 0.25, cursor: 'pointer' }}
                                    />
                                );
                            })}
                        </Box>
                    </>
                )}
            </Box>
        );
    };

    const formatExtraFilters = (r: ReportInstance) => {
        const filters = getExtraFilters(r);
        return filters.map(([key, values]) => formatFilters('extra_filters', key, values)).join('; ');
    };

    const renderExtraFilters = (r: ReportInstance) => {
        const filters = getExtraFilters(r);
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {filters.map(([key, values]) => renderFilters('extra_filters', key, values))}
            </Box>
        );
    };

    const columns: GridColDef[] = useMemo(() => {
        return [
            {
                field: 'created_at',
                headerName: t('content.reports.table.execution_date'),
                ...getDateTimeColumnType(locale),
                renderCell: (params) => {
                    return dayjs(params.value as Date).format(profile.dateTimeFormat);
                },
                flex: 1,
            },
            {
                field: 'scope',
                type: 'singleSelect',
                valueOptions: ['daily', 'weekly', 'monthly'].map((scope) => formatScope(scope)),
                headerName: t('content.reports.form.scope.header'),
                valueGetter: (params: GridRenderCellParams) => formatScope(params.value),
                flex: 1,
            },
            {
                field: 'vehicles',
                headerName: t('content.reports.columns.vehicles'),
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams) =>
                    renderFilters('filter', ...deviceFilter(params.row.filters), true),
                flex: 1,
            },
            {
                field: 'rules',
                headerName: t('content.reports.columns.rules'),
                sortable: false,
                renderCell: (params: GridRenderCellParams) => renderExtraFilters(params.row.instance),
                flex: 1,
            },
        ];
    }, []);

    const rows = useMemo(() => {
        return props.reportInstances.map((reportInstance) => {
            return {
                id: reportInstance.pk,
                created_at: dayjs.unix(reportInstance.created_at).toDate(),
                type: reportInstance.report_type,
                scope: formatScope(reportInstance.report_scope),
                vehicles: formatFilters(
                    'filter',
                    ...deviceFilter(reportInstance.filters as Record<string, Array<string> | string>),
                    true,
                ),
                rules: formatExtraFilters(reportInstance),
                filters: reportInstance.filters,
                instance: reportInstance,
            };
        });
    }, [props.reportInstances, props.geofences]);

    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            <StyledDataGrid
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'created_at', sort: 'desc' }],
                    },
                }}
                id="reports-data-grid"
                data-testid="reports-data-grid"
                loading={props.isLoading}
                getRowHeight={() => 'auto'}
                columns={columns}
                rows={rows}
                disableRowSelectionOnClick
                hideFooter
                disableVirtualization={isMobile || process.env.JEST_WORKER_ID !== undefined}
                getCellClassName={(params) => `${params.field}-cell`}
                onRowClick={(params) => {
                    props.onRowClick();
                    history.push(`/reports/${REPORT_TYPE_TO_URL_PARAM[params.row.type]}/run/${params.row.id}`);
                }}
                slots={{
                    noRowsOverlay: () => (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="overline">{t('content.reports.no_instances')}</Typography>
                        </Box>
                    ),
                }}
                slotProps={{
                    columnsPanel: {
                        sx: {
                            '& .MuiDataGrid-panelFooter button:first-child': {
                                display: 'none',
                            },
                        },
                    },
                }}
                localeText={{
                    ...gridLocalization[i18n.languages[0]],
                    filterPanelRemoveAll: t('table.remove_all'),
                    columnMenuManageColumns: t('table.manage_columns'),
                    unpin: t('table.unpin'),
                }}
                columnHeaderHeight={42}
                sx={{
                    height: '100%',
                    width: '100%',
                    pb: 2,
                    '& .MuiDataGrid-main': {
                        borderRadius: '4px',
                        boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.25)',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        borderBottom: 'none',
                        color: palette.neutral[100],
                        '& .MuiSvgIcon-root': {
                            fill: palette.neutral[100],
                        },
                    },
                    '& .MuiDataGrid-row:hover': {
                        cursor: 'pointer',
                    },
                    '& .MuiDataGrid-cell--editing': {
                        padding: '0 !important',
                    },
                    '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-row--dynamicHeight': { minHeight: '52px !important' },
                    '&.MuiDataGrid-root .MuiDataGrid-cell': { py: '6px' },
                    backgroundColor: palette.neutral[50],
                    minHeight: 0,
                }}
            />
        </Box>
    );
};

export default PastReports;
